import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'

import { Button, Card, DataTable, Notice, Pill, ProblemNotice, StatusPill, type Column } from '../../components/ui'
import {
  type AdminLlmUsage, type AdminUserView, type Job, getUser, getUserLlmUsage, listUserJobs,
  listUserRecordings, requestUserExport,
} from '../../lib/api'
import { formatDateTime, formatSessionDate } from '../../lib/format'
import { usageLine } from '../../lib/usageLine'
import { useRetryLanding } from '../../lib/useRetryLanding'

/**
 * One account, in detail (§5.4).
 *
 * Account state, recordings, jobs, and Daily Status **metadata** — never raw
 * signal. That is not a promise this screen keeps; it is a property of the
 * contract. `/admin/users/{id}/recordings` returns `RecordingRecord`s whose
 * `session_dir` and `daily_status_uri` are URIs, and `tests/unit/test_uri_audit.py`
 * enforces structurally that nothing above a device adapter resolves one.
 */
export function UserDetail() {
  const { userId = '' } = useParams()
  const [user, setUser] = useState<AdminUserView | null>(null)
  const [recordings, setRecordings] = useState<RecordingRow[]>([])
  const [jobs, setJobs] = useState<Job[]>([])
  // agent-1.1 S2. Its own failure, like the lists': a cost that could not be read
  // must never render as "0 turns".
  const [usage, setUsage] = useState<{ value: AdminLlmUsage | null; failed: boolean }>({
    value: null,
    failed: false,
  })
  const [isLoading, setLoading] = useState(true)
  // See `Users.tsx` for the shape and the reason.
  const [error, setError] = useState<{ text: string; cause: unknown; retry?: () => void } | null>(null)
  /**
   * THE LISTS' OWN FAILURES, which were being discarded.
   *
   * The `.catch(() => ({ items: [] }))` below was right about one thing and
   * wrong about what follows from it: a list failure should not hide the
   * account, but swallowing it entirely rendered "No recordings." and "Nothing
   * has been queued for this account." -- POSITIVE FACTUAL CLAIMS about an
   * operator's account, on the strength of a request that failed. That is not a
   * missing error state; it is an error state converted into a false empty one.
   */
  const [listErrors, setListErrors] = useState<{ recordings: unknown; jobs: unknown }>({
    recordings: null,
    jobs: null,
  })
  const [reloadToken, setReloadToken] = useState(0)
  const retryAll = useCallback(() => {
    setLoading(true)
    setReloadToken((n) => n + 1)
  }, [])
  /**
   * ONE landing for all three notices on this screen, because all three retry
   * with `retryAll`, which sets `isLoading` and early-returns the WHOLE screen
   * (see below). The focused "Try again" unmounts with everything else, so this
   * is the plain-unmount route rather than the `busy` one -- none of these
   * three passes `retryBusy` at all, and `Button`'s restore is never armed.
   *
   * `failing` is the union: any of the three notices showing means the landing
   * is still pending.
   */
  const failing = error !== null || listErrors.recordings !== null || listErrors.jobs !== null
  const [landOn, armLanding] = useRetryLanding(failing)
  const [exportJob, setExportJob] = useState<Job | null>(null)
  const [isExporting, setExporting] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        // In parallel: three independent reads, and a failure in the lists
        // should not hide the account itself.
        const [account, recs, js, cost] = await Promise.all([
          getUser(userId),
          // Still caught, so a list failure does not hide the account -- but the
          // failure is KEPT rather than replaced with an empty page, so the
          // section can say it could not be read instead of claiming there is
          // nothing to read.
          listUserRecordings(userId).then(
            (page) => ({ page, error: null as unknown }),
            (err: unknown) => ({ page: { items: [], next_cursor: null }, error: err }),
          ),
          listUserJobs(userId).then(
            (page) => ({ page, error: null as unknown }),
            (err: unknown) => ({ page: { items: [], next_cursor: null }, error: err }),
          ),
          getUserLlmUsage(userId).then(
            (value) => ({ value, failed: false }),
            () => ({ value: null, failed: true }),
          ),
        ])
        if (cancelled) return
        setUser(account)
        setRecordings(recs.page.items as RecordingRow[])
        setJobs(js.page.items as Job[])
        setListErrors({ recordings: recs.error, jobs: js.error })
        setUsage(cost)
        setError(null)
      } catch (err) {
        if (!cancelled) setError({ text: 'That account could not be loaded.', cause: err, retry: retryAll })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [userId, reloadToken, retryAll])

  if (isLoading) {
    return <div className="table-state" role="status"><span className="spinner" aria-hidden="true" /><span>Loading…</span></div>
  }
  if (error || !user) {
    return (
      <div className="admin-page stack stack--wide">
        {error ? (
          <ProblemNotice error={error.cause} onRetry={error.retry} landing={armLanding}>{error.text}</ProblemNotice>
        ) : (
          <Notice tone="error">That account could not be found.</Notice>
        )}
        <Link className="auth__link" to="/admin/users">Back to users</Link>
      </div>
    )
  }

  const recordingColumns: Column<RecordingRow>[] = [
    { key: 'session', header: 'Session', render: (r) => <span className="mono-cell">{r.session_id}</span> },
    { key: 'date', header: 'Date', render: (r) => formatSessionDate(r.session_date) },
    { key: 'device', header: 'Device', render: (r) => <span className="mono-cell">{r.device_id}</span> },
    { key: 'status', header: 'Status', render: (r) => <Pill dot>{r.status}</Pill> },
    {
      key: 'daily',
      header: 'Daily Status',
      render: (r) => (r.daily_status_uri ? <Pill tone="green" dot>stored</Pill> : <span>—</span>),
    },
  ]

  const jobColumns: Column<Job>[] = [
    { key: 'kind', header: 'Kind', render: (j) => <span className="mono-cell">{j.kind}</span> },
    { key: 'status', header: 'Status', render: (j) => <Pill dot>{j.status}</Pill> },
    { key: 'progress', header: 'Progress', align: 'end', render: (j) => `${j.progress_pct}%` },
    { key: 'created', header: 'Created', align: 'end', render: (j) => formatDateTime(j.created_at) },
  ]

  return (
    <div className="admin-page stack stack--wide">
      {/* The landing target. Once the read works this header is what says which
          account is on screen, so a keyboard user who pressed "Try again" ends
          up at the answer rather than on `<body>`. */}
      <header className="page retry-landing" ref={landOn} tabIndex={-1}>
        <Link className="auth__link" to="/admin/users">← Users</Link>
        <h1 className="mono-cell">{user.email ?? user.user_id}</h1>
        <div className="detail-badges">
          <StatusPill status={user.status} />
          <Pill tone={user.role === 'admin' ? 'cyan' : 'neutral'}>
            {user.role === 'admin' ? 'Administrator' : 'User'}
          </Pill>
          {!user.cognito_username && <Pill tone="amber" dot>Not linked to Cognito</Pill>}
        </div>
      </header>

      <Card eyebrow="Account">
        <dl className="detail-grid">
          <Detail label="User id" value={user.user_id} mono />
          <div><dt>Experiment access</dt><dd><Link className="auth__link" to={`/admin/settings#experiments.enabled_users`}>Manage allowed user ids</Link></dd></div>
          <Detail label="Cognito sub" value={user.cognito_sub ?? '—'} mono />
          <Detail label="Email verified" value={user.email_verified ? 'Yes' : 'No'} />
          <Detail label="Created" value={formatDateTime(user.created_at)} />
          <Detail label="Approved" value={formatDateTime(user.approved_at)} />
          <Detail label="Last sign-in" value={formatDateTime(user.last_login_at)} />
          <Detail label="Role changed" value={formatDateTime(user.role_changed_at)} />
          <Detail label="Deleted" value={formatDateTime(user.deleted_at)} />
          <Detail label="Purge after" value={formatDateTime(user.purge_after)} />
          <Detail label="Assistant and notes, 30 days" value={usageLine(usage)} />
        </dl>
      </Card>

      <section className="detail-section">
        <h2>Recordings</h2>
        <p className="page__lead">
          Metadata and artifact references only. Raw signal is not served to
          administrators — these are URIs, and nothing above a device adapter resolves one.
        </p>
        <DataTable
          caption={`Recordings for ${user.email ?? user.user_id}`}
          columns={recordingColumns}
          rows={recordings}
          rowKey={(r) => r.recording_id}
          empty={<><strong>No recordings.</strong><span>This account has not uploaded a session.</span></>}
        />
        {listErrors.recordings != null && (
          <ProblemNotice error={listErrors.recordings} onRetry={retryAll} landing={armLanding}>
            The recordings for this account could not be read, so the list above is not
            evidence that there are none.
          </ProblemNotice>
        )}
      </section>

      <section className="detail-section">
        <h2>Jobs</h2>
        <DataTable
          caption={`Jobs for ${user.email ?? user.user_id}`}
          columns={jobColumns}
          rows={jobs}
          rowKey={(j) => j.job_id}
          empty={<><strong>No jobs.</strong><span>Nothing has been queued for this account.</span></>}
        />
        {listErrors.jobs != null && (
          <ProblemNotice error={listErrors.jobs} onRetry={retryAll} landing={armLanding}>
            The jobs for this account could not be read, so the list above is not evidence
            that there are none.
          </ProblemNotice>
        )}
      </section>

      <section className="detail-section">
        <h2>Export</h2>
        <p className="page__lead">
          Queues a copy of everything held about this account, and returns a job to poll.
        </p>
        <Button
          variant="secondary"
          busy={isExporting}
          onClick={() => {
            setExporting(true)
            void requestUserExport(user.user_id)
              .then(setExportJob)
              .catch((err: unknown) => setError({ text: 'The export could not be queued.', cause: err }))
              .finally(() => setExporting(false))
          }}
        >
          Request an export
        </Button>
        {exportJob && (
          <Notice tone={exportJob.status === 'failed' ? 'warn' : 'info'}>
            Job <span className="mono-cell">{exportJob.job_id}</span> — {exportJob.status}.
            {exportJob.error_problem?.code === 'feature.not_implemented' && (
              <>
                {' '}The export executor lands in Sprint 1.11, so this terminates as
                <span className="mono-cell"> failed</span> today rather than producing an
                archive. The request itself is recorded in the audit log.
              </>
            )}
          </Notice>
        )}
      </section>
    </div>
  )
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="detail-grid__item">
      <dt className="record__label">{label}</dt>
      <dd className={`record__value${mono ? ' mono-cell' : ''}`}>{value}</dd>
    </div>
  )
}

/** The fields this screen reads from a RecordingRecord. */
interface RecordingRow {
  recording_id: string
  session_id: string
  session_date: string
  device_id: string
  status: string
  daily_status_uri?: string | null
}

